import { Box, Button, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import React from 'react';
import { Controller } from 'react-hook-form';
import customToasterUpdater from '../toast/CustomToastUpdater';
import { useTranslation } from 'react-i18next';
import UploadDialog from './Dialog/uploadDialog';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

function ArchiveLandingScreen({
  drawRectangleHandlerRef,
  handleFileUpload,
  archiveUiElementsRef,
  selectDataTypeRef,
  toggleSelectDataType,
  formControls,
  filterStartDate,
  setShowDateRangeNote,
  setFilterLastDate,
  filterLastDate,
  setFilterStartDate,
  showDateRangeNote,
  renderDtIcon,
}: {
  drawRectangleHandlerRef: any;
  handleFileUpload: any;
  archiveUiElementsRef: any;
  selectDataTypeRef: any;
  toggleSelectDataType: any;
  formControls: any;
  filterStartDate: any;
  setShowDateRangeNote: any;
  setFilterLastDate: any;
  filterLastDate: any;
  setFilterStartDate: any;
  showDateRangeNote: any;
  renderDtIcon: any;
}) {
  const { t } = useTranslation('orderImagery');
  const { control, setValue, getValues, errors } = formControls;

  return (
    <Box
      className="archive-landing-screen"
      sx={{
        height: 'calc(100vh - 271px)',
        overflow: 'auto',
        backgroundColor: '#2D3C4A',
        borderRadius: '10px',
        padding: '20px',
      }}
    >
      <Box
        display="flex"
        className="landing-content"
        justifyContent="space-between"
        flexDirection="column"
        gap="8px"
        marginBottom="16px"
      >
        <Typography variant="body1">{t('drawAnAreaTitle', 'Draw an area')}</Typography>
        <Typography variant="body2">
          {t('useSearchBar', 'Use the search bar above to find a specific location or')}
        </Typography>
        <Box display="flex" justifyContent="space-between" gap="5px">
          <Button
            color="secondary"
            variant="outlined"
            fullWidth
            size="medium"
            onClick={() => {
              drawRectangleHandlerRef.current && drawRectangleHandlerRef.current();
            }}
          >
            {t('drawAnAreaTitle', 'Draw an area')}
          </Button>
          <UploadDialog isArchiveLandingScreen={true} handleFileUpload={handleFileUpload} />
        </Box>
      </Box>

      <Box
        display="flex"
        className="landing-content"
        justifyContent="space-between"
        flexDirection="column"
        gap="8px"
        margin="16px 0px 16px 0px"
      >
        <Typography variant="body1">{t('dataTypeTitle', 'Data Type')}</Typography>
        <Typography variant="body2">
          {t('dataTypeDesc', 'Select your preferred data type')}
        </Typography>
        <Box display="flex" className="dt-options-wrapper" flexWrap="wrap" gap="8px">
          {archiveUiElementsRef?.current &&
            archiveUiElementsRef?.current?.data?.data_types?.map((item: any, index: any) => {
              return (
                <Box
                  key={index}
                  className={`dt-options ${selectDataTypeRef.current?.includes(item?.value) ? 'active' : ''}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                    justifyContent: 'center',
                  }}
                  onClick={() => {
                    toggleSelectDataType(item);
                  }}
                >
                  {renderDtIcon(item?.icon)}
                  <Typography
                    className="dt-options-labels"
                    variant="body2"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {t(`ui-elements:${item?.label_tKey}`, `${item?.label}`)}
                    {item?.sub_label && (
                      <span className="dt-options-sub-labels">{item?.sub_label}</span>
                    )}
                  </Typography>
                </Box>
              );
            })}
        </Box>
      </Box>

      <Box
        display="flex"
        className="landing-content"
        id="ls-date-range-wrapper"
        justifyContent="space-between"
        flexDirection="column"
        gap="8px"
        margin="16px 0px 16px 0px"
      >
        <Typography variant="body1">{t('dateRange', 'Date Range')}</Typography>
        <Typography variant="body2">
          {t(
            'recommendedDateRangeMsg',
            'Recommended date based on the data type to maximize the results'
          )}
        </Typography>
        <Box display="flex" justifyContent="space-between" gap="5px" id="ls-date-range">
          <Controller
            control={control}
            name="startDate"
            defaultValue={filterStartDate}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  className="filled-datepicker"
                  value={field.value ? moment(field.value) : moment(filterStartDate)}
                  disableFuture
                  slotProps={{
                    textField: {
                      inputProps: { disabled: true }, // Disable typing
                    },
                  }}
                  onChange={(value: any) => {
                    setShowDateRangeNote(true);
                    const newStartDate = moment(value).format('YYYY-MM-DD');
                    field.onChange(newStartDate);
                    const newLastDate = getValues('lastDate');
                    if (
                      moment(newLastDate).isAfter(newStartDate) &&
                      !moment(newLastDate).isSame(newStartDate)
                    ) {
                      setFilterStartDate(newStartDate);
                    } else {
                      customToasterUpdater(t('common:selectDateRange'), 'valid-st');
                    }
                  }}
                />
              </LocalizationProvider>
            )}
          />
          <Controller
            name="lastDate"
            control={control}
            defaultValue={filterLastDate}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  className="filled-datepicker"
                  value={field.value ? moment(field.value) : moment(filterLastDate)}
                  slotProps={{
                    textField: {
                      inputProps: { disabled: true }, // Disable typing
                    },
                  }}
                  disableFuture
                  onChange={(value: any) => {
                    setShowDateRangeNote(true);
                    const newLastDate = moment(value).format('YYYY-MM-DD');
                    const newStartDate = getValues('startDate');
                    field.onChange(newLastDate);
                    if (
                      moment(newLastDate).isAfter(newStartDate) &&
                      !moment(newLastDate).isSame(newStartDate)
                    ) {
                      setFilterLastDate(newLastDate);
                    } else {
                      customToasterUpdater(t('common:selectDateRange'), 'valid-edt');
                    }
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Box>
        {showDateRangeNote && (
          <Box
            display="flex"
            justifyContent="space-between"
            gap="6px"
            sx={{
              backgroundColor: 'rgba(73, 97, 120, 0.5)',
              padding: '8px',
              marginTop: '8px',
              borderRadius: '8px',
            }}
          >
            <InfoOutlinedIcon color="secondary" fontSize="small" />
            <Typography variant="caption">
              {t(
                'changingDefaultDateMsg',
                'Changing the default date range might reduce the probability of seeing relevant results'
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default ArchiveLandingScreen;
